import { DataService, SubscribeForNotificationsType } from '../data/src';
import { PromiseHelper } from '../helpers';
import { PushAdapterBase } from './push-adapter-base';
import { PushAdapterErrorType, PushAdapterSettings } from '../models/notifications.model';

export abstract class ServiceWorkerPushAdapterBase<TSubscription> extends PushAdapterBase<TSubscription> {
  constructor(
    notificationsType: SubscribeForNotificationsType,
    coreDataService: DataService,
    channelIdentifier: string,
    private serviceWorkerUrl: string,
    settings: PushAdapterSettings,
    clientIdentifier: string,
  ) {
    super(notificationsType, coreDataService, channelIdentifier, settings, clientIdentifier);
  }

  protected override async enablePushNotificationsCore(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        if (await this.isDenied()) {
          this.notifyStateChanged(false, false);
          throw PushAdapterErrorType.UnableToEnablePushNotifications;
        } else {
          if (!this.hasPushSubscriptionIdentifier) {
            this.notifyStateChanged(true, false);
            await this.requestNotificationAccessIfNeeded(
              async () => {
                let reg = await navigator.serviceWorker.getRegistration(this.serviceWorkerUrl);
                if (!reg) {
                  reg = await navigator.serviceWorker.register(this.serviceWorkerUrl);
                }
                let serviceWorker: ServiceWorker;
                if (reg?.installing) {
                  serviceWorker = reg?.installing;
                } else if (reg?.waiting) {
                  serviceWorker = reg?.waiting;
                } else if (reg?.active) {
                  serviceWorker = reg?.active;
                }
                if (serviceWorker) {
                  if (await this.tryGetPushSubscription(serviceWorker, reg)) {
                    this.registerForNotificationMessages(reg);
                  } else {
                    this.notifyStateChanged(false, true);
                  }
                } else {
                  this.notifyStateChanged(false, false);
                }
              },
              async () => this.notifyStateChanged(false, false),
              () => this.notifyStateChanged(false, true),
              (errorType) => {
                this.settings.onError(errorType);
                this.notifyStateChanged(false, true);
                throw errorType;
              },
            );
          } else {
            this.notifyStateChanged(true, true);
          }
        }
      } else {
        this.notifyStateChanged(false, false);
      }
    } catch (err) {
      this.notifyStateChanged(false, false);
      this.settings.onError(PushAdapterErrorType.UnableToEnablePushNotifications, err);
      throw err;
    }
  }

  private async tryGetPushSubscription(serviceWorker: ServiceWorker, reg: ServiceWorkerRegistration): Promise<boolean> {
    if (serviceWorker.state === 'activated') {
      if (!this.hasPushSubscriptionIdentifier) {
        await this.notifyPushSubscription(await this.getPushSubscription(reg));
      }
      return true;
    }

    return new Promise<boolean>((resolve) => {
      serviceWorker.addEventListener('statechange', async () => {
        if (serviceWorker.state === 'activated') {
          if (!this.hasPushSubscriptionIdentifier) {
            await this.notifyPushSubscription(await this.getPushSubscription(reg));
          }
          resolve(true);
        }
      });
    });
  }

  private onServiceWorkerMessage(reg: ServiceWorkerRegistration, event: MessageEvent): void {
    if (event.data?.type === 'NOTIFICATION_CLICKED') {
      if (this.settings.onNotificationClicked) {
        this.settings.onNotificationClicked(event.data?.payload);
      }
    } else if (event.data?.type === 'BACKGROUND_NOTIFICATION') {
      this.onBackgroundMessageReceived(reg, event.data?.payload);
    }
  }

  private registerForNotificationMessages(reg: ServiceWorkerRegistration): void {
    //important: use navigator.serviceWorker here (not "active" or other serviceWorker instance!)
    navigator.serviceWorker.addEventListener('message', (event) => this.onServiceWorkerMessage(reg, event));
  }

  private unregisterForNotificationMessages(reg: ServiceWorkerRegistration): void {
    //important: use navigator.serviceWorker here (not "active" or other serviceWorker instance!)
    navigator.serviceWorker.removeEventListener('message', (event) => this.onServiceWorkerMessage(reg, event));
  }

  protected override async unsubscribe(): Promise<void> {
    await super.unsubscribe();
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      this.unregisterForNotificationMessages(reg);
      reg.unregister();
    }
  }

  private async getPushSubscription(reg: ServiceWorkerRegistration): Promise<TSubscription> {
    if (await this.isGranted()) {
      return await this.getPushSubscriptionCore(reg);
    } else if (await this.isDenied()) {
      this.notifyStateChanged(false, false);
      return null;
    } else {
      return await this.requestNotificationAccessIfNeeded(
        () => this.getPushSubscriptionCore(reg),
        async () => this.notifyStateChanged(false, false),
        () => this.notifyStateChanged(false, true),
        (errorType) => this.settings.onError(errorType),
      );
    }
  }

  private async getPushSubscriptionCore(reg: ServiceWorkerRegistration): Promise<TSubscription> {
    let sub: TSubscription = null;
    let subErrorType: PushAdapterErrorType = undefined;
    let subErrorObj: any = undefined;
    try {
      sub = await this.getCurrentSubscription(reg);
      if (!sub) {
        sub = await PromiseHelper.timeout(this.subscribeCore(reg), 8000);
      }
      if (!sub) {
        subErrorType = PushAdapterErrorType.UnableToSubscribeToNotificationService;
      }
    } catch (error) {
      subErrorType = PushAdapterErrorType.UnableToSubscribeToNotificationService;
      subErrorObj = error;
    }

    if (subErrorType) {
      this.active(false);
      this.settings.onError(subErrorType, subErrorObj);
    }

    return sub;
  }

  private async requestNotificationAccessIfNeeded<T>(
    onGranted: () => Promise<T>,
    onDenied: () => Promise<void>,
    onCancelled: () => void,
    onError: (type: PushAdapterErrorType) => void,
  ): Promise<T> {
    if (!(await this.isGranted())) {
      try {
        await this.requestPermission();
      } catch (errorType) {
        onError(errorType);
      }
    }
    if (await this.isGranted()) {
      return await onGranted();
    } else if (await this.isDenied()) {
      await onDenied();
    } else {
      onCancelled();
    }
    return null;
  }

  private async subscribe(reg: ServiceWorkerRegistration): Promise<TSubscription> {
    let sub = await this.getCurrentSubscription(reg);
    if (!sub) {
      sub = await PromiseHelper.timeout(this.subscribeCore(reg), 8000);
    }
    return sub;
  }

  protected abstract getCurrentSubscription(reg: ServiceWorkerRegistration): Promise<TSubscription>;
  protected abstract subscribeCore(reg: ServiceWorkerRegistration): Promise<TSubscription>;
  protected abstract onBackgroundMessageReceived(reg: ServiceWorkerRegistration, data: any): void;
}
